/**
 * Typed selectors over `state.onboarding.userMetadata`. Introduced by
 * subscription-tiers-rewrite Group 1 — adding selectors instead of having every
 * component reach into the metadata blob directly. Start small (one new field
 * per this change); grow as call sites migrate.
 */
import type { RootState } from "src/store/RootStore";
import { Plan } from "src/domain/plan";
import { Entitlements } from "src/domain/entitlements";

export interface UserMetadata {
  id?: string;
  subscription_type?: string | null;
  legal_review_credits_remaining?: number;
  legal_review_credits_total?: number;
  legal_review_credits_used?: number;
  free_threads_remaining?: number | null;
  // ... other fields read directly by callers for now; migrate as needed.
  [key: string]: unknown;
}

type StateShape = Pick<RootState, "onboarding"> | { onboarding?: { userMetadata?: UserMetadata } };

const EMPTY_METADATA: UserMetadata = {};

export const selectUserMetadata = (state: StateShape): UserMetadata => {
  const onboarding = (state as { onboarding?: { userMetadata?: UserMetadata } }).onboarding;
  return onboarding?.userMetadata ?? EMPTY_METADATA;
};

export const selectSubscriptionType = (state: StateShape): string | null => {
  return selectUserMetadata(state).subscription_type ?? null;
};

export const selectFreeThreadsRemaining = (state: StateShape): number | null => {
  const value = selectUserMetadata(state).free_threads_remaining;
  return value === undefined ? null : value;
};

/**
 * THE read path for tier decisions. Returns a frozen Plan singleton, so the
 * reference is stable across renders until the tier actually changes — no
 * memoization needed. Do not store the returned Plan in Redux and do not
 * spread it (see src/domain/plan/Plan.ts header).
 */
export const selectPlan = (state: StateShape): Plan => {
  return Plan.from(selectSubscriptionType(state));
};

/**
 * Memoized on the metadata reference: Entitlements is NOT a singleton, so a
 * bare `Entitlements.from()` inside useSelector would return a fresh instance
 * every call and defeat referential stability. The metadata blob reference
 * only changes when the store writes new metadata.
 */
let lastEntitlementsMetadata: UserMetadata | null = null;
let lastEntitlements: Entitlements | null = null;
export const selectEntitlements = (state: StateShape): Entitlements => {
  const metadata = selectUserMetadata(state);
  if (metadata !== lastEntitlementsMetadata || lastEntitlements === null) {
    lastEntitlementsMetadata = metadata;
    lastEntitlements = Entitlements.from(metadata);
  }
  return lastEntitlements;
};
